import classnames from 'classnames';
import React from 'react';
import ReactSelect, { components, GroupBase, MenuPlacement, MultiValueProps, OptionProps, SelectComponentsConfig } from 'react-select';

import Icon from '../Icon';

import './Select.css';

// Define the option type for the select
export interface SelectOption {
	value: string;
	label: string;
	placeHolder?: string;
}

export type SelectValue = SelectOption | string[] | string;

// Use correct MultiValueProps from react-select
const MultiValue = (props: MultiValueProps<SelectOption, true>) => {
	const values = props.selectProps.value as SelectOption[];
	const lastValue = values[values.length - 1];
	let label = props.data.label;
	if (lastValue.label !== label) {
		label += ', ';
	}
	return <span>{label}</span>;
};

// Use correct OptionProps from react-select
const Option = (props: OptionProps<SelectOption, boolean>) => {
	return (
		<components.Option {...props}>
			<div className="flex items-center">
				<div className="h-2 w-2">{props.isSelected ? <Icon name={'checkbox-active'} /> : <Icon name={'checkbox-default'} />}</div>
				<label id={props.data.value} className="ml-3 mt-1">
					<span>{props.data.label}</span>
				</label>
			</div>
		</components.Option>
	);
};

// Define SelectProps interface
export interface SelectProps {
	id: string;
	className?: string;
	closeMenuOnSelect?: boolean;
	hideSelectedOptions?: boolean;
	isClearable?: boolean;
	isDisabled?: boolean;
	isMulti?: boolean;
	isSearchable?: boolean;
	onChange: (value: SelectValue, action: string) => void;
	options: SelectOption[];
	placeholder?: string;
	noIcons?: boolean;
	menuPlacement?: MenuPlacement;
	components?: SelectComponentsConfig<SelectOption, boolean, GroupBase<SelectOption>>;
	value?: SelectValue;
	isWhiteColor?: boolean;
	customHeightMenu?: number;
}

const Select = ({
	id,
	className = '',
	closeMenuOnSelect = true,
	hideSelectedOptions = false,
	isClearable = true,
	isDisabled = false,
	isMulti = false,
	isSearchable = true,
	onChange,
	options,
	placeholder,
	noIcons = false,
	menuPlacement = 'auto' as MenuPlacement,
	components = {},
	value = [],
	isWhiteColor = false,
	customHeightMenu = 100
}: SelectProps) => {
	const _noIconComponents = {
		DropdownIndicator: () => null,
		IndicatorSeparator: () => null
	};
	let _components: SelectComponentsConfig<SelectOption, boolean, GroupBase<SelectOption>> = isMulti ? { Option, MultiValue } : {};
	_components = noIcons ? { ..._components, ..._noIconComponents } : { ..._components, ...components };

	const selectedOptions: SelectOption[] = [];

	// Map array of values to an array of selected options
	if (value && Array.isArray(value)) {
		value.forEach((val) => {
			const found = options.find((opt) => opt.value === val);
			if (found) {
				selectedOptions.push(JSON.parse(JSON.stringify(found)));
			}
		});
	}

	return (
		<ReactSelect
			inputId={`input-${id}`}
			className={classnames(
				className,
				`${isWhiteColor ? 'customSelectWhiteColor__wrapper' : 'customSelect__wrapper'} ohif-select flex flex-1 flex-col`
			)}
			data-cy={`input-${id}`}
			classNamePrefix={isWhiteColor ? 'customSelectWhiteColor' : 'customSelect'}
			isDisabled={isDisabled}
			isClearable={isClearable}
			isMulti={isMulti}
			isSearchable={isSearchable}
			menuPlacement={menuPlacement}
			closeMenuOnSelect={closeMenuOnSelect}
			hideSelectedOptions={hideSelectedOptions}
			components={_components}
			placeholder={placeholder}
			options={options}
			maxMenuHeight={customHeightMenu}
			blurInputOnSelect={true}
			menuPortalTarget={document.body}
			styles={{
				menuPortal: (base) => ({ ...base, zIndex: 9999 })
			}}
			value={value && Array.isArray(value) ? selectedOptions : value}
			onChange={(selectedOptions, { action }) => {
				const newSelection = Array.isArray(selectedOptions)
					? selectedOptions.reduce((acc, curr) => acc.concat([curr]), [])
					: selectedOptions;
				onChange(newSelection, action);
			}}
		/>
	);
};

export default Select;
